import { Space } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, PlusOutlined } from '@ant-design/icons';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import Button from 'components/new-ui/base/button/Button';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import CustomersTable from 'views/customers/components/customers-table/CustomersTable';
import useSession from 'hooks/useSession';
import { hasPermissions } from 'config/roles/hasPermissions';
import { useRequestPromise } from 'hooks/useRequest';
import { exportCustomers, listArchivedCustomers, listCustomers } from 'store/customers/client';
import useCustomersFilters from './hooks/useCustomersFilters';
import { Customer, CustomerSortParameter } from 'store/customers/customer';
import { getTableChangeParams, SortDirection, SortDirections } from 'utils/tableHelpers';
import ArchiveCustomerModal from './components/ArchiveCustomerModal';
import UnarchiveCustomer from './components/UnarchiveCustomerModal';
import CreateNewCustomerDrawer from './components/create-customer-drawer/CreateNewCustomerDrawer';
import useCustomerMutations from './hooks/useCustomerMutations';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { triggerBrowserDownload } from 'utils/download-utils';

type DrawerMode = { open: false } | { open: true; mode: 'create' } | { open: true; mode: 'edit'; customer: Customer };

type ModalState = { open: false } | { open: true; type: 'archive' | 'unarchive'; customer: Customer };

// Note: Will rename to Parties in a follow-up work item
const CustomersPage = () => {
    const navigate = useNavigate();
    const { userInfo } = useSession();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { appliedParams, setQueryInputVal, queryInputVal, setPage, setSize, setSortState } = useCustomersFilters();

    // Currently, to get the archived customers, we need to call the other API (/customers/archived)
    // When the normal API allows filtering by ARCHIVED param, this state can be removed
    const [archived, setArchived] = useState(false);
    const [drawer, setDrawer] = useState<DrawerMode>({ open: false });
    const [modal, setModal] = useState<ModalState>({ open: false });

    const {
        data,
        isLoading,
        refetch: refreshCustomers,
    } = useRequestPromise(
        () => {
            return archived ? listArchivedCustomers(appliedParams) : listCustomers(appliedParams);
        },
        {},
        [appliedParams, archived]
    );

    const { isMutating, archive, unarchive } = useCustomerMutations(refreshCustomers);

    const canUpdate = hasPermissions(userInfo, 'customers', 'UPDATE');
    const canArchive = hasPermissions(userInfo, 'customers', 'ARCHIVE');
    const currentMode = drawer.open ? drawer.mode : 'create';
    const { page, size } = appliedParams;

    const openCreate = () => setDrawer({ open: true, mode: 'create' });

    const openEdit = (customer: Customer) => setDrawer({ open: true, mode: 'edit', customer });

    const closeDrawer = () => setDrawer({ open: false });

    const openArchive = (customer: Customer) => setModal({ open: true, type: 'archive', customer });

    const openUnarchive = (customer: Customer) => setModal({ open: true, type: 'unarchive', customer });

    const closeModal = () => setModal({ open: false });

    const handleArchiveCustomer = async () => {
        if (!modal.open || modal.type !== 'archive') return;
        await archive(modal.customer.id, { onFinally: closeModal });
    };

    const handleUnarchiveCustomer = async () => {
        if (!modal.open || modal.type !== 'unarchive') return;
        await unarchive(modal.customer.id, { onFinally: closeModal });
    };

    const handleDrawerSuccess = (mode: 'create' | 'edit') => {
        Notification({
            type: 'success',
            messageTitle: mode === 'create' ? 'Party Created' : 'Party Updated',
            description:
                mode === 'create'
                    ? 'The party has been created successfully.'
                    : 'The party has been updated successfully.',
            placement: 'top',
        });

        refreshCustomers();
        closeDrawer();
    };

    const handleDrawerError = (error: unknown, mode: 'create' | 'edit') => {
        Notification({
            type: 'error',
            messageTitle: mode === 'create' ? 'Creation Failed' : 'Update Failed',
            description: getErrorMessage(error) || 'Something went wrong.',
            placement: 'top',
        });
    };

    const handleExportCustomers = async () => {
        try {
            const blob = await exportCustomers();

            triggerBrowserDownload(blob, 'parties.csv');

            Notification({
                type: 'success',
                messageTitle: 'Parties data exported successfully.',
                description: 'Your CSV file with the parties has started downloading.',
                placement: 'top',
            });
        } catch (error) {
            Notification({
                type: 'error',
                messageTitle: 'Could not export parties data.',
                description: getErrorMessage(error) || 'Something went wrong.',
                placement: 'top',
            });
        }
    };

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Parties',
                path: '',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    return (
        <>
            <StyledPageWrapper>
                <PageHeader
                    title="Parties"
                    icon={<UserOutlined />}
                    sidebar={
                        <Space size={16}>
                            <ArchiveFilter
                                value={archived ? 'archived' : 'active'}
                                onChange={(val) => {
                                    setArchived(val === 'active' ? false : true);
                                    setPage(0);
                                    setQueryInputVal('');
                                }}
                            />
                            <Button onClick={handleExportCustomers}>Export Parties</Button>
                            <Button onClick={openCreate} type="primary" icon={<PlusOutlined />}>
                                New Party
                            </Button>
                        </Space>
                    }
                />

                <Space style={{ width: '100%', marginTop: 24 }} direction="vertical" size={24}>
                    <Space size={8}>
                        <SearchBar
                            inputPlaceholder="Seach Parties"
                            value={queryInputVal}
                            onSearch={(val) => {
                                setQueryInputVal(val);
                                setPage(0);
                            }}
                            onClear={() => {
                                setQueryInputVal('');
                                setPage(0);
                            }}
                        />
                    </Space>

                    <CustomersTable
                        showActions
                        canUpdate={canUpdate}
                        canArchive={canArchive}
                        customers={data?.list}
                        loading={isLoading}
                        onEdit={openEdit}
                        onArchive={openArchive}
                        onUnarchive={openUnarchive}
                        onChange={(antPagination, _filters, sorter) => {
                            const raw = getTableChangeParams({ pagination: antPagination, sorter });

                            const { page, size, sortParameter, sortDirection } = (raw || {}) as {
                                page?: number;
                                size?: number;
                                sortParameter?: string;
                                sortDirection?: SortDirection;
                            };

                            setPage((p) => (typeof page === 'number' ? page : p));
                            setSize((p) => (typeof size === 'number' ? size : p));

                            if (!sortParameter || !sortDirection) {
                                setSortState({ sort: undefined, sortDirection: undefined });
                            } else {
                                setSortState({
                                    sort: sortParameter as CustomerSortParameter,
                                    sortDirection: sortDirection as SortDirections,
                                });
                            }
                        }}
                        pagination={{
                            current: (typeof page === 'number' ? page : 0) + 1,
                            pageSize: size,
                            total: data?.total,
                            position: ['bottomCenter'],
                            showSizeChanger: true,
                            hideOnSinglePage: true,
                        }}
                        onRow={(record) => {
                            return {
                                style: { cursor: 'pointer' },
                                onClick: () => navigate(`/customers/${record.id}`),
                            };
                        }}
                    />
                </Space>
            </StyledPageWrapper>

            <ArchiveCustomerModal
                onArchive={handleArchiveCustomer}
                onClose={closeModal}
                open={modal.open && modal.type === 'archive'}
                isArchiving={isMutating}
            />

            <UnarchiveCustomer
                onUnarchive={handleUnarchiveCustomer}
                onClose={closeModal}
                open={modal.open && modal.type === 'unarchive'}
                isUnarchiving={isMutating}
            />

            <CreateNewCustomerDrawer
                isEditing={drawer.open && drawer.mode === 'edit'}
                title={drawer.open && drawer.mode === 'edit' ? 'Edit Party' : 'New Party'}
                open={drawer.open}
                onClose={closeDrawer}
                handleOk={() => handleDrawerSuccess(currentMode)}
                handleCreateError={(err: any) => handleDrawerError(err, 'create')}
                handleEditError={(err: any) => handleDrawerError(err, 'edit')}
                refreshCustomers={refreshCustomers}
                customer={drawer.open && drawer.mode === 'edit' ? drawer.customer : undefined}
                handleEdit={() => handleDrawerSuccess(currentMode)}
            />
        </>
    );
};

export default CustomersPage;
